import { EhrName } from "@metriport/shared-internal";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getPatientMapping, linkPatient } from "../../api/ehr";
import { capture } from "../../shared/capture";
import useMetriportToast from "./toast";

const PATIENT_LOOKUP_QUERY_KEY = "patient-lookup";

type PatientLookupParams = {
  ehrId: string;
  externalId: string;
};

export function usePatientLookupQuery(params?: PatientLookupParams) {
  return useQuery({
    queryKey: [PATIENT_LOOKUP_QUERY_KEY, params?.ehrId, params?.externalId],
    queryFn: async () => {
      if (!params) throw new Error("Missing lookup params");
      return await getPatientMapping(params);
    },
    enabled: !!params?.ehrId && !!params?.externalId,
    retry: false,
  });
}

type LinkPatientParams = {
  cxId: string;
  patientId: string;
  practiceId: string;
  ehr: EhrName;
};

export function useLinkPatientMutation() {
  const toast = useMetriportToast();
  return useMutation<void, Error, LinkPatientParams>({
    mutationFn: async ({ cxId, patientId, practiceId, ehr }) => {
      await linkPatient({ cxId, patientId, practiceId, ehr });
    },
    onSuccess: (_, variables) => {
      toast.success({ title: `${variables.ehr} patient linked successfully` });
    },
    onError: (err, variables) => {
      capture.error(err, {
        extra: { context: `${variables.ehr.toLowerCase()}.linkPatient`, cxId: variables.cxId },
      });
      toast.error({ title: `Failed to link ${variables.ehr} patient` });
    },
  });
}
